/**
 * RefPicker - the dropdown for the name lists on the opportunity and project
 * forms (customers, engineers, approval managers, project managers).
 *
 * It replaced the browser's own <select> (10/8/26) because a select cannot
 * put anything on an item: here each name carries a red x that takes it off
 * the list (onRemove - only passed to people allowed to change the lists),
 * and a search box at the top finds a name in a long list. "+ Add new..." is
 * the last item, as before. Removing a name never changes a record: the
 * field keeps what it holds.
 *
 *   value      the field's value ('' = nothing picked)
 *   options    the names offered
 *   onChange   (name) - a name picked ('' for the "-" item)
 *   onAddNew   () - "+ Add new..." (the caller asks for the name)
 *   onRemove   (name) -> Promise<boolean> - the red x; omit to hide it
 *   placeholder  what the closed box shows with no value ('-')
 *   clearable  offer the "-" item (single pickers); false for "+ Add engineer..."
 *   label      the accessible name of the box ("Approval Manager")
 */
import { useEffect, useMemo, useRef, useState } from 'react';

const RED = '#C62828';

export default function RefPicker({
  value = '',
  options,
  onChange,
  onAddNew,
  onRemove,
  placeholder = '-',
  clearable = true,
  label,
  style,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(''); // the name being removed
  const [err, setErr] = useState('');
  const wrap = useRef(null);

  // a click anywhere else, or Escape, closes it
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc, true);
    };
  }, [open]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((n) => n.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
    setQ('');
  };
  const remove = async (name) => {
    if (!window.confirm(`Remove "${name}" from this list?\n\nRecords that already use it keep it. "+ Add new..." brings it back.`)) return;
    setBusy(name);
    setErr('');
    const ok = await onRemove(name);
    setBusy('');
    if (!ok) setErr(`Could not remove ${name}. Try again.`);
  };

  const row = { display: 'flex', alignItems: 'center', borderBottom: '1px solid #F3F3F0' };
  const item = {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    background: 'none',
    border: 'none',
    padding: '7px 10px',
    fontSize: 11.5,
    fontFamily: 'inherit',
    color: '#1F2328',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  return (
    // the box takes the input style's flex, so it fills a flex row like the input it replaced
    <div ref={wrap} style={{ position: 'relative', minWidth: 0, flex: style && style.flex }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => {
          setOpen((o) => !o);
          setErr('');
        }}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          textAlign: 'left',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: value ? 'inherit' : '#777' }}>
          {value || placeholder}
        </span>
        <span aria-hidden="true" style={{ fontSize: 9, color: '#777' }}>
          ▼
        </span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 60,
            marginTop: 2,
            background: '#fff',
            border: '1px solid #D8DED4',
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            minWidth: 200,
          }}
        >
          <input
            autoFocus
            aria-label={`Search ${label || 'the list'}`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: 'none',
              borderBottom: '1px solid #EEE',
              padding: '8px 10px',
              fontSize: 11.5,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <div role="listbox" aria-label={`${label || 'Names'} list`} style={{ maxHeight: 240, overflowY: 'auto' }}>
            {clearable && !q && (
              <div style={row}>
                <button type="button" role="option" aria-selected={!value} style={{ ...item, color: '#777' }} onClick={() => pick('')}>
                  -
                </button>
              </div>
            )}
            {shown.map((n) => (
              <div key={n} style={{ ...row, background: n === value ? '#F0F7EE' : undefined }}>
                <button type="button" role="option" aria-selected={n === value} style={item} onClick={() => pick(n)}>
                  {n}
                </button>
                {onRemove && (
                  <button
                    type="button"
                    aria-label={`Remove ${n} from the list`}
                    title={`Remove ${n} from this list`}
                    disabled={busy === n}
                    onClick={() => remove(n)}
                    style={{
                      flexShrink: 0,
                      background: 'none',
                      border: 'none',
                      color: RED,
                      fontSize: 15,
                      lineHeight: 1,
                      padding: '4px 10px',
                      cursor: busy === n ? 'wait' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {shown.length === 0 && <div style={{ padding: '8px 10px', fontSize: 11, color: '#999' }}>No match.</div>}
            <div style={row}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                style={{ ...item, color: '#1B5E20', fontWeight: 700 }}
                onClick={() => {
                  setOpen(false);
                  setQ('');
                  onAddNew();
                }}
              >
                ＋ Add new…
              </button>
            </div>
          </div>
          {err && (
            <div role="alert" style={{ padding: '6px 10px', fontSize: 10.5, color: RED, borderTop: '1px solid #EEE' }}>
              {err}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
